"use client";
import {
  Box,
  Button,
  ButtonIcon,
  Dropdown,
  Icon,
  Tags,
  Text,
} from "@oneloop/jopijs";
import React, { useEffect, useState } from "react";
import style from "./headerFI.module.scss";
import ExportToPdf from "./exportToPDF/ExportarToPdf";
import {
  setEditedData,
  setEditedDataBatch,
  setIsEdition,
  setIsImagesEdition,
  setShowData,
  setShowModalEditionUnsaved,
  setThereIsChanges,
  showToast,
} from "@/store/slices/components/fichaInfo/FichaInfoSlice";
import ToolTip from "@/components/ToolTip/ToolTip";
import Trans from "@/components/shared/Trans";
import translations from "../FichaInfo.trans.json";
import areEqual from "../utilities/helpers";
import { useSelector, useDispatch } from "react-redux";
import Skeletons from "../skelenton/Skeleton";
import { saveProfileEdition } from "@/store/slices/components/fichaInfo/services";
import cleanEditionData from "../utilities/cleanEditedData";
import { useRouter, usePathname } from "next/navigation";

const formatString = (text, boldPart) => {
  const parts = text.split(boldPart);
  return (
    <div>
      {parts[0]}
      <strong>{boldPart}</strong>
      {parts[1]}
    </div>
  );
};

export default function HeaderFI() {
  const {
    isEdition,
    isImagesEdition,
    thereIsChanges,
    initialEditionData,
    edited_data,
    for_colleague,
    is_contact,
    loading,
    hash,
    TOKKO_HOST,
    from_opennavent,
    showData,
  } = useSelector((ste) => ste.FichaInfoSlice);

  const dispatch = useDispatch();
  const pathname = usePathname();
  const { replace } = useRouter();
  const { has_edition } = edited_data;
  const fichaInfoVersions = [
    {
      id: 1,
      content: {
        name: Trans(translations, "PropiertyFile"),
        value: Trans(translations, "Original"),
        stateValue: "origin_data",
      },
    },
    {
      id: 2,
      content: {
        name: formatString(
          `${Trans(translations, "PropiertyFile")} (${Trans(
            translations,
            "Edited"
          )})`,
          Trans(translations, "Edited")
        ),
        value: Trans(translations, "LastEdition"),
        stateValue: "edited_data",
      },
    },
  ];
  const [value, setValue] = useState(fichaInfoVersions[0]);
  const [saveLoading, setSaveLoading] = useState(false);
  const isntEditionAndEditionSaved =
    !isEdition && has_edition && !for_colleague && !is_contact;
  const userCantEdit = for_colleague || is_contact || from_opennavent;

  const propertyFileToogle = (type) => {
    if (type === "origin") {
      setValue(fichaInfoVersions[0]);
      dispatch(setShowData("origin_data"));
      return;
    }
    setValue(fichaInfoVersions[1]);
    dispatch(setShowData("edited_data"));
  };

  useEffect(() => {
    dispatch(setThereIsChanges(!areEqual(initialEditionData, edited_data)));
  }, [initialEditionData, edited_data]);

  useEffect(() => {
    if (has_edition === null) return;
    if (has_edition && showData === "edited_data") propertyFileToogle();
    else propertyFileToogle("origin");
  }, [thereIsChanges, has_edition, isEdition]);

  useEffect(() => {
    if (is_contact && has_edition && showData === "edited_data")
      propertyFileToogle();
  }, [is_contact, has_edition]);

  const handleFichaInfoSelected = (id) => {
    dispatch(setShowData(fichaInfoVersions[id - 1].content.stateValue));
  };

  const handleCancel = () => {
    if (thereIsChanges) return dispatch(setShowModalEditionUnsaved(true));
    dispatch(setIsEdition(false));
  };

  const handleEditionImagesCancel = () => {
    dispatch(
      setEditedDataBatch({
        pictures: initialEditionData.pictures,
        videos: initialEditionData.videos,
      })
    );
    dispatch(setIsImagesEdition(false));
  };

  const handleSave = async () => {
    if (isImagesEdition) return dispatch(setIsImagesEdition(false));

    if (!thereIsChanges) return dispatch(setIsEdition(false));

    setSaveLoading(true);
    const { error } = await dispatch(
      saveProfileEdition({
        data: cleanEditionData(edited_data),
        hash,
        TOKKO_HOST,
      })
    );

    if (error)
      return dispatch(
        showToast({
          type: "danger",
          text: Trans(translations, "SavingError"),
        })
      );

    dispatch(showToast({ text: Trans(translations, "ChangesSaved") }));
    updateUrl();
    propertyFileToogle();
    dispatch(setIsEdition(false));
    dispatch(setEditedData({ key: "has_edition", value: true }));
    setSaveLoading(false);
  };

  const updateUrl = () => {
    const timestamp = new Date().getTime();
    const finalPath = `${pathname}?v=${timestamp}`;
    replace(finalPath);
  };

  if (loading)
    return (
      <Box className={style.headerContentContainer}>
        <Skeletons height={26} width={100} />
      </Box>
    );

  return (
    <Box className={style.headerContentContainer}>
      {isntEditionAndEditionSaved ? (
        <Dropdown className={style.headerDropdown} width="auto" height="26px">
          <Dropdown.Button
            id="dropdownButton"
            variant="dropdownDefault"
            border="1px solid #a2d4e5"
            text={value.content.name}
            filled={value.content.name}
          />
          <Dropdown.Items clickClose={true}>
            {fichaInfoVersions.map((user) => (
              <Dropdown.Default
                key={user.id}
                onClick={() => {
                  handleFichaInfoSelected(user.id);
                  setValue(user);
                }}
              >
                {user.content.value}
              </Dropdown.Default>
            ))}
          </Dropdown.Items>
        </Dropdown>
      ) : (
        <Tags
          padding="4px 8px"
          variant="default"
          height="26px"
          borderColor="#a2d4e5"
          className={style.titleTag}
          noClick
        >
          {Trans(translations, "PropiertyFile")}
        </Tags>
      )}
      {!userCantEdit && (
        <>
          {!isEdition && (
            <Box __css={{ display: "flex", gap: "16px" }}>
              <ExportToPdf />
              <Box className={style.desktopButtonsContainer}>
                <ToolTip
                  place="bottom"
                  title={Trans(translations, "ToolTipInfoEdit")}
                  maxWidth="300"
                >
                  <ButtonIcon
                    className={`${style.headerEditButton} ${style.headerButtonHoverStyle}`}
                    width="max-content"
                    text={Trans(translations, "EditFile")}
                    icon="icon-editarfichainfo"
                    variant={["primaryOutlined", "small"]}
                    onClick={() => {
                      dispatch(setIsEdition(true));
                    }}
                  />
                </ToolTip>
              </Box>
              <Box className={style.mobileButtonsContainer}>
                <ButtonIcon
                  icon="icon-editarfichainfo"
                  className={`${style.mobileEditIcon} ${style.headerButtonHoverStyle}`}
                  variant={["primaryOutlined", "iconLarge"]}
                  onClick={() => {
                    dispatch(setIsEdition(true));
                  }}
                />
              </Box>
            </Box>
          )}
          {isEdition && (
            <Box>
              <Box
                __css={{ alignItems: "center" }}
                className={style.desktopButtonsContainer}
              >
                {!isImagesEdition ? (
                  <Text
                    onClick={handleCancel}
                    variant={["body.fontSize12"]}
                    style={{ cursor: "pointer" }}
                  >
                    {Trans(translations, "Close")}
                  </Text>
                ) : (
                  <Text
                    onClick={handleEditionImagesCancel}
                    variant={["body.fontSize12"]}
                    style={{ cursor: "pointer" }}
                  >
                    {Trans(translations, "Cancel")}
                  </Text>
                )}
                {
                  <Button
                    variant={[
                      `${
                        thereIsChanges && !saveLoading
                          ? "primaryOutlined"
                          : "outlinedDisabled"
                      }`,
                      "small",
                    ]}
                    className={`${style.headerSaveButton} ${style.headerButtonHoverStyle}`}
                    onClick={handleSave}
                    data-testid="save-button"
                    borderColor="var(--neutralGray5)"
                  >
                    {Trans(
                      translations,
                      !isImagesEdition ? "SaveChanges" : "Save"
                    )}
                  </Button>
                }
              </Box>
              <Box className={style.mobileButtonsContainer}>
                <Icon
                  icon="icon-cerrar"
                  fontSize="16px"
                  data-testid="cancel-button"
                  onClick={() =>
                    isImagesEdition
                      ? dispatch(setIsImagesEdition(false))
                      : handleCancel()
                  }
                />
                <ButtonIcon
                  icon="icon-guardar"
                  className={`${style.mobileEditIcon} ${style.headerButtonHoverStyle}`}
                  variant={[
                    `${
                      thereIsChanges ? "primaryOutlined" : "outlinedDisabled"
                    }`,
                    "iconLarge",
                  ]}
                  onClick={handleSave}
                />
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
